// src/components/personal/sections/OrdersSection/components/OrderInfo/OrderDetailsTab.tsx
import React from 'react';
import { Order } from '../../types/types';
import { TEXT_STYLES, COMPONENT_STYLES } from '@/theme';
// Заменяем импорт formatDate на импорт из useOrdersInfo
import { formatDate } from '../../hooks/useOrdersInfo';
import { formatPhoneNumber } from '@/utils/Formatters';
import { getOrderStatusOptions, getSalesChannelTextStatic } from '../../utils/statusHelpers';
import { useTranslation } from 'react-i18next'; // Добавляем импорт useTranslation
import { TFunction } from 'i18next'; // Импортируем TFunction из i18next
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

interface OrderDetailsTabProps {
  order: Order;
  disabled: boolean;
  onStatusChange: (e: React.ChangeEvent<HTMLSelectElement>) => Promise<void>;
  t?: TFunction; // Делаем t и i18n опциональными
  i18n?: {
    language: string;
  };
}

const OrderDetailsTab: React.FC<OrderDetailsTabProps> = ({
  order,
  disabled,
  onStatusChange,
  t: tProp,
  i18n: i18nProp
}) => {
  // Используем хук useTranslation внутри компонента для получения t и i18n
  const { t: tHook, i18n: i18nHook } = useTranslation();
  
  // Используем переданные пропсы, если они есть, иначе используем хук
  const t = tProp || tHook;
  const i18n = i18nProp || i18nHook;
  
  const { formatPrice } = useBusinessCurrency();

  // Безопасный доступ к user_data
  const userData = order.user_data || {};

  const getDeliveryAddress = () => {
    if (!userData) return null;

    // Приоритет прямого поля address
    if (userData.address) {
      return userData.address;
    }

    // Сборка адреса из компонентов
    const addressParts = [];
    if (userData.street) addressParts.push(userData.street);
    if (userData.house) addressParts.push(`дом ${userData.house}`);
    if (userData.apartment) addressParts.push(`кв. ${userData.apartment}`);

    return addressParts.length > 0 ? addressParts.join(', ') : null;
  };

  const getDeliveryComment = () => {
    if (!userData) return null;

    // Список возможных ключей для комментария 
    const commentKeys = ['comment', 'комментарий', ' комментарий ', 'комментарий '];

    for (const key of commentKeys) {
      if (userData[key]) {
        return userData[key];
      }
    }

    return null;
  };

  const deliveryAddress = getDeliveryAddress() || '—';
  const deliveryComment = getDeliveryComment() || '—';

  // Безопасный доступ к cost_data
  const costData = order.cost_data || {};
  const total = costData.total || order.total_cost || 0;

  const InfoItem: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
    <div className="mb-2">
      <label className={`block ${TEXT_STYLES.small} text-text`}>{label}</label>
      <div className={TEXT_STYLES.body}>{value || '—'}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Информация о клиенте */}
        <div>
          <h4 className={`${TEXT_STYLES.small} text-text mb-2`}>
            {t('order_details.client_info')}
          </h4>
          <InfoItem 
            label={t('table.client')} 
            value={order.client_name || t('table.unknown_client')} 
          />
          <InfoItem 
            label={t('table.phone')} 
            value={order.client_phone ? formatPhoneNumber(order.client_phone) : '—'} 
          />
        </div>

        {/* Информация о заказе */}
        <div>
          <h4 className={`${TEXT_STYLES.small} text-text mb-2`}>
            {t('order_details.order_info')}
          </h4>
          <InfoItem 
            label={t('table.date')} 
            value={formatDate(order.created_at, true, i18n.language)} 
          />
          <InfoItem 
            label={t('table.platform')} 
            value={getSalesChannelTextStatic(order.platform, t)} 
          />
          <div className="mb-2">
            <label className={`block ${TEXT_STYLES.small} text-text`}>
              {t('table.total')}
            </label>
            <div className={`${TEXT_STYLES.body} font-medium text-primary`}>
              {formatPrice(total)}
            </div>
          </div>
          <div className="mb-2">
            <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
              {t('table.status')}
            </label>
            <select
              value={order.status}
              onChange={onStatusChange}
              disabled={disabled}
              className={`${COMPONENT_STYLES.input.base} w-full`}
            >
              {getOrderStatusOptions(t)
                .filter(option => option.value !== 'all')
                .map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Информация о доставке */}
      <div>
        <h4 className={`${TEXT_STYLES.small} text-text mb-2`}>
          {t('order_details.delivery_info')}
        </h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
              {t('order_details.delivery_address')}
            </label>
            <div className={TEXT_STYLES.body}>
              {deliveryAddress}
            </div>
          </div>
          
          <div>
            <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
              {t('order_details.comment')}
            </label>
            <div className={TEXT_STYLES.body}>
              {deliveryComment}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsTab;